import { useCallback, useEffect, useRef, useState } from "react";
import usersService, { User } from "../services/usersService";

interface UseUsersParams {
  pageSize?: number;
}

export const useUsers = ({ pageSize: initialPageSize = 10 }: UseUsersParams = {}) => {
  // Data state
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Sort state
  const [sortColumn, setSortColumn] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchColumn, setSearchColumn] = useState<"" | "id" | "name" | "email" | "providerName">("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didMountSearchRef = useRef(false);
  const didMountFiltersRef = useRef(false);
  const didMountSortRef = useRef(false);
  const fetchRequestIdRef = useRef(0);

  // Role filter state - Set for multi-select
  type RoleType = "ADMIN" | "INVESTOR" | "PROVIDER" | "ADVISOR";
  const [roleFilters, setRoleFilters] = useState<Set<RoleType>>(new Set());

  // Toggle helper for role filter
  const toggleRoleFilter = (role: RoleType) => {
    setRoleFilters(prev => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
  };

  // Debounce search
  useEffect(() => {
    if (!didMountSearchRef.current) {
      didMountSearchRef.current = true;
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 1000);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Reset page when filters change (skip initial render)
  useEffect(() => {
    if (!didMountFiltersRef.current) {
      didMountFiltersRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [searchColumn, roleFilters, pageSize]);

  // Reset page when sort changes (skip initial render)
  useEffect(() => {
    if (!didMountSortRef.current) {
      didMountSortRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [sortColumn, sortDirection]);

  const fetchUsers = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;
    try {
      setLoading(true);
      setError(null);

      const response = await usersService.findUsers({
        pageNumber: currentPage,
        pageSize,
        orderByColumn: sortColumn,
        orderByDirection: sortDirection,
        searchString: debouncedSearchQuery || undefined,
        searchColumn: searchColumn || undefined,
        roles: roleFilters.size > 0 ? Array.from(roleFilters) : undefined,
      });

      if (fetchRequestIdRef.current !== requestId) return;
      setUsers(response.items);
      setTotalItems(response.totalItems);
      setTotalPages(response.totalPages);
    } catch (err) {
      if (fetchRequestIdRef.current !== requestId) return;
      setError(err instanceof Error ? err.message : "Failed to fetch users");
    } finally {
      if (fetchRequestIdRef.current === requestId) {
        setLoading(false);
        setHasLoaded(true);
      }
    }
  }, [currentPage, pageSize, sortColumn, sortDirection, debouncedSearchQuery, searchColumn, roleFilters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  return {
    users,
    loading,
    hasLoaded,
    error,
    pagination: {
      currentPage,
      setCurrentPage,
      pageSize,
      setPageSize,
      totalItems,
      totalPages,
    },
    sorting: {
      sortColumn,
      sortDirection,
      handleSort,
    },
    search: {
      searchQuery,
      setSearchQuery,
      searchColumn,
      setSearchColumn,
    },
    filters: {
      roleFilters,
      setRoleFilters,
      toggleRoleFilter,
    },
    actions: {
      fetchUsers,
      setError,
    },
  };
};
